import React from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import appPages from 'app/appPages';
import withBanner from 'app/blocks/banner-messages/withBanner';
import { Layout } from 'app/blocks/blocks';
import { ID, withCodes2 } from 'app/blocks/common/codes';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import { cleanToken, compose, getQueryParam, getQueryParamSafe } from 'app/blocks/common/utils';
import withAnalytics, { withAnalyticsContext } from 'app/blocks/common/withAnalytics';
import withAuthRequiredPage from 'app/blocks/common/withAuthRequiredPage';
import withScrollToTop from 'app/blocks/common/withScrollToTop';
import withTopMenu from 'app/blocks/common/withTopMenu';
import withUnauthRequiredPage, {
    withExplicitLogin as withExplicitLoginPage,
} from 'app/blocks/common/withUnauthRequiredPage';
import Message from 'app/blocks/message/message';
import { logout, refreshUser } from 'app/blocks/middleware/authentication';
import { navigateToDashboard } from 'app/blocks/middleware/dashboard';
import withUrlParams from 'app/blocks/middleware/url-params';
import AlertsPage from 'app/pages/alerts/alerts-page';
import ArticlePage from 'app/pages/article';
import ArticleAssignmentByJournal from 'app/pages/assign-article/_by-journal/assign-article_by-journal';
import ArticleAssignmentById from 'app/pages/assign-article/article-assignment-by-id/article-assignment-by-id';
import Dashboard from 'app/pages/dashboard';
import EmailAlertsPage from 'app/pages/email-alerts/email-alerts-page';
import LicenseSigningProcessPage from 'app/pages/license-signing-process/LicenseSigningProcessPage';
import FinalizeLoginPage, { isFinalizeLoginPage } from 'app/pages/login/finalize-login-page';
import LoginPage from 'app/pages/login/login-page';
import RecoverPasswordPage from 'app/pages/login/recover-password-page';
import RedirectLogin from 'app/pages/login/redirect-login';
import Orders from 'app/pages/orders/orders';
import PubEditorReview from 'app/pages/pubeditor-review/pubeditor-review';
import EmailVerificationPage from 'app/pages/registration/email-verification-page';
import RegistrationPage from 'app/pages/registration/registration-page';
import AdvanceContentSearch from 'app/pages/search/_advanced/advance_content_search';
import MissingArticle from 'app/pages/search/_advanced/missing-article-details/MissingArticle';
import MissingArticleDetails from 'app/pages/search/_advanced/missing-article-details/MissingArticleDetails';
import ContentSearch from 'app/pages/search/content_search';
import Version from 'app/pages/version/version';
import { navigate } from 'app/route/history';
import tempStorage from 'app/route/tempStorage';
import { fromHashToBrowser } from './routesConverter';
import { isMock } from '../blocks/middleware/config';

const withNamedAuth = page => Component => withAuthRequiredPage(Component, page);
const withAuthForPage = page => compose(withBanner, withNamedAuth(page), withScrollToTop);
const withUnauth = compose(withBanner, withUnauthRequiredPage, withScrollToTop);
const withExplicitLogin = compose(withBanner, withExplicitLoginPage, withScrollToTop);
const withFinishLogin = Component => {
    if (isFinalizeLoginPage()) {
        return () => <FinalizeLoginPage />;
    }
    return Component;
};

const BASE_NAME = 'index.html';
const SPALink = link => `/${BASE_NAME}/${link}`;

function HomeRoute() {
    if (isFinalizeLoginPage()) {
        return <FinalizeLoginPage />;
    }

    window.location.replace('/home.html');

    return null;
}

function LegacyRoute() {
    const idx = window.location.href.indexOf('index.html');
    const relativePath = window.location.href.substring(idx);
    const route = fromHashToBrowser(relativePath);
    window.location.replace(route);
    return null;
}

const LoginRoute = withExplicitLogin(props => {
    return <LoginPage {...props} queryParams={getQueryParam(useLocation().search)} skipNotify />;
});

const RegistrationRoute = compose(withUrlParams)(props => <RegistrationPage {...props} />, true);

function RegistrationByInviteRoute(props) {
    const { articleId } = getQueryParamSafe(useLocation().search, true);
    return <RegistrationRoute {...props} articleId={articleId} invitationToken={cleanToken(useParams().token || '')} />;
}

const RecoverPasswordRoute = compose(
    withFinishLogin,
    withAnalytics(appPages.RECOVER_PASSWORD),
    withUnauth,
)(props => <RecoverPasswordPage {...props} />);

const AlertsRoute = compose(
    withAnalytics(appPages.ALERTS),
    withAuthForPage(appPages.ALERTS),
    withTopMenu,
)(props => <AlertsPage {...props} />);

const VerifyEmailRoute = compose(
    withBanner,
    withScrollToTop,
)(props => <EmailVerificationPage {...props} verifyEmailUuid={cleanToken(useParams().token)} />);

const MessageRoute = compose(
    withBanner,
    withScrollToTop,
)(props => <Message {...props} {...getQueryParam(useLocation().search)} />);

const ResetPasswordRoute = compose(
    withAnalytics(appPages.RESET_PASSWORD),
    withUnauth,
    withCodes2(ID.RECOVER_PASSWORD),
)(props => (
    <RecoverPasswordPage
        {...props}
        queryParams={getQueryParam(useLocation().search)}
        uuid={encodeURIComponent(cleanToken(useParams().uuid))}
    />
));

const DashboardPage = compose(
    withAuthForPage(appPages.DASHBOARD),
    withTopMenu,
)(props => <Dashboard {...props} focusOnArticle={getQueryParam(useLocation().search).focusOnArticle} />);

// eslint-disable-next-line consistent-return
const DashboardRoute = props => {
    if (isFinalizeLoginPage()) {
        return <FinalizeLoginPage />;
    }

    if (isMock()) {
        return <DashboardPage {...props} />;
    }

    navigateToDashboard();
};

const ArticlePageRoute = withAuthForPage(appPages.ARTICLE_PAGE)(
    withTopMenu(props => <ArticlePage {...props} articleId={useParams().articleId} />),
);

const SearchRoute = compose(
    withBanner,
    withScrollToTop,
)(() => {
    const { text } = getQueryParam(useLocation().search, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const context = React.useMemo(() => tempStorage.get(), [text]);

    return <ContentSearch key={text} context={context} text={text} />;
});

const AdvanceSearchRoute = compose(
    withBanner,
    withScrollToTop,
)(props => {
    const context = React.useMemo(() => tempStorage.get(), []);
    const { jid, type } = getQueryParam(useLocation().search);

    return <AdvanceContentSearch {...props} key={`${jid}-${type}`} context={context} jid={jid} type={type} />;
});

const MissingArticleRoute = compose(
    withAnalytics(appPages.MISSING_ARTICLE),
    withAuthForPage(appPages.MISSING_ARTICLE),
)(props => <MissingArticle {...props} />);

const MissingArticleDetailsRoute = compose(
    withAnalytics(appPages.MISSING_ARTICLE_DETAILS),
    withAuthForPage(appPages.MISSING_ARTICLE_DETAILS),
)(props => <MissingArticleDetails {...props} />);

const VersionRoute = withAnalytics(appPages.VERSION)(props => <Version {...props} />);

const EmailAlertsRoute = withAnalytics(appPages.EMAIL_ALERTS)(props => (
    <EmailAlertsPage {...props} {...getQueryParam(useLocation().search)} />
));

const CheckAuthAndRedirectRoute = withCodes2(
    ID.ERROR,
    ID.LOGIN,
)(props => {
    const { link } = getQueryParam(useLocation().search, true);
    const isLinkValid = link && link[0] === '/';

    const [isLoading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (isLinkValid) {
            (async () => {
                try {
                    const { loggedIn } = await refreshUser();

                    if (loggedIn) {
                        window.location.href = link;
                    } else {
                        setLoading(false);

                        showDialog.error({
                            closeBtnLabel: props.l('LOGIN.pageName'),
                            message: props.l('ERROR.SESSION_HAS_BEEN_EXPIRED'),
                            onClose() {
                                logout();
                            },
                        });
                    }
                } catch (error) {
                    setLoading(false);

                    showDialog.error(error);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isLinkValid) {
        return <Message message={props.l('ERROR.WRONG_REDIRECT_LINK')} />;
    }
    if (isLoading) {
        return <Layout isLoading />;
    }

    return null;
});

function DashboardCallbackRoute(props) {
    const { search } = useLocation();
    if (/[?&]message=/.test(search)) {
        const params = getQueryParam(search, true);
        const message = decodeURIComponent(params.message.replace(/\+/g, '%20'));
        const code = decodeURIComponent(params.code);
        const refId = decodeURIComponent(params.refId);

        return <Dashboard {...props} code={code} error={message} refId={refId} />;
    }

    return null;
}

const ReviewPDFRoute = withBanner(props => {
    const { search } = useLocation();
    if (/[?&]message=/.test(search)) {
        const params = getQueryParam(search, true);
        const message = decodeURIComponent(params.message.replace(/\+/g, '%20'));
        const code = decodeURIComponent(params.code);
        const refId = decodeURIComponent(params.refId);

        return <PubEditorReview {...props} code={code} error={message} refId={refId} />;
    }

    return null;
});

const PubEditorReviewRoute = compose(
    withAnalytics(appPages.PUB_EDITOR_REVIEW),
    withBanner,
)(() => {
    const props = getQueryParam(useLocation().search, true);

    if (props.token) {
        props.token = cleanToken(props.token);
    }

    return <PubEditorReview {...props} />;
});

const LicenseSigningRoute = withBanner(() => {
    const { articleId, step, substep } = useParams();
    return <LicenseSigningProcessPage articleId={articleId} step={step} substep={substep} />;
});

const OrdersRoute = compose(
    withAnalyticsContext(appPages.ORDERS),
    withBanner,
    withNamedAuth(appPages.ORDERS),
    withTopMenu,
)(() => <Orders error={getQueryParam(useLocation().search).error} />);

const OrdersFocusOnArticleRoute = compose(
    withAnalyticsContext(appPages.ORDERS_FOCUS_ON_ARTICLE),
    withBanner,
    withNamedAuth(appPages.ORDERS_FOCUS_ON_ARTICLE),
)(() => {
    return <Orders error={getQueryParam(useLocation().search).error} focusOnArticle={useParams().articleId} />;
});

function cancelArticleAssignment(context) {
    if (typeof context.query === 'string') {
        navigate(`/index.html/search?text=${context.query}`, undefined, context);
    } else if (context.jid) {
        navigate(`/index.html/advancedsearch?jid=${context.jid}&type=article`, undefined, context);
    } else {
        navigate(`/index.html/advancedsearch?type=article`, undefined, context);
    }
}

const AssignArticleRoute = compose(
    withBanner,
    withNamedAuth(appPages.ASSIGN_ARTICLE),
)(() => {
    const params = getQueryParam(useLocation().search);
    const context = React.useMemo(() => tempStorage.get(), []);
    let cancel;

    if (context) {
        cancel = () => cancelArticleAssignment(context);
    } else {
        cancel = () => {
            navigateToDashboard();
        };
    }

    if (params.articleId) {
        return (
            <ArticleAssignmentById
                articleId={params.articleId}
                cancel={cancel}
                gotoDashboard={() => {
                    navigateToDashboard();
                }}
            />
        );
    }
    if (params.journalId) {
        return (
            <ArticleAssignmentByJournal
                cancel={cancel}
                gotoDashboard={() => {
                    navigateToDashboard();
                }}
                journalId={params.journalId}
            />
        );
    }

    return null;
});

const NoMatchRoute = withBanner(() => {
    if (isFinalizeLoginPage()) {
        return <FinalizeLoginPage />;
    }
    if (useLocation().hash.length) {
        return <LegacyRoute />;
    }
    return <Navigate to="/index.html/dashboard" />;
});

export default [
    <Route key="Home" Component={HomeRoute} path="/" />,
    <Route key="redirectHome" Component={RedirectLogin} path={SPALink('errorRedirectHome')} />,
    <Route key="Login" Component={withFinishLogin(LoginRoute)} path={SPALink('login/*')} />,

    <Route
        key="RegistrationByInvite"
        Component={RegistrationByInviteRoute}
        path={SPALink('register-invite/:token/*')}
    />,
    <Route key="Registration" Component={RegistrationRoute} path={SPALink('register')} />,

    <Route key="RecoverPassword" Component={RecoverPasswordRoute} path={SPALink('recover/*')} />,
    <Route key="Alerts" Component={withFinishLogin(AlertsRoute)} path={SPALink('alerts')} />,
    <Route key="VerifyEmail" Component={VerifyEmailRoute} path={SPALink('verify/:token/*')} />,
    <Route key="Message" Component={MessageRoute} path={SPALink('message/*')} />,
    <Route key="ResetPassword" Component={ResetPasswordRoute} path={SPALink('resetPassword/:uuid/*')} />,
    <Route key="Dashboard" Component={withFinishLogin(DashboardRoute)} path={SPALink('dashboard')} />,
    <Route key="ArticlePage" Component={withFinishLogin(ArticlePageRoute)} path={SPALink('article/:articleId/*')} />,
    <Route key="Search" Component={SearchRoute} path={SPALink('search/*')} />,
    <Route key="MissingArticle" Component={withFinishLogin(MissingArticleRoute)} path={SPALink('missing-article')} />,
    <Route
        key="MissingArticleDetails"
        Component={withFinishLogin(MissingArticleDetailsRoute)}
        path={SPALink('get-help-on-article/*')}
    />,
    <Route
        key="MissingArticleDetailsRex"
        Component={withFinishLogin(MissingArticleDetailsRoute)}
        path={SPALink('get-help-on-article/:system/*')}
    />,
    <Route key="AdvanceSearch" Component={AdvanceSearchRoute} path={SPALink('advancedsearch/*')} />,
    <Route key="Version" Component={VersionRoute} path={SPALink('version/*')} />,
    <Route key="CheckAuthAndRedirect" Component={CheckAuthAndRedirectRoute} path={SPALink('checkAuthAndRedirect/*')} />,

    <Route
        key="DashboardCallbackRouteArticle"
        Component={DashboardCallbackRoute}
        path={SPALink('dashboard/articlePdf/')}
    />,
    <Route
        key="DashboardCallbackRouteInvoice"
        Component={DashboardCallbackRoute}
        path={SPALink('dashboard/invoicePdf/')}
    />,
    <Route
        key="DashboardCallbackRouteLicense"
        Component={DashboardCallbackRoute}
        path={SPALink('license/printLicensePdf/')}
    />,
    <Route key="ReviewPDFRoute" Component={ReviewPDFRoute} path={SPALink('license/licenseCopyPdf/')} />,
    <Route key="PubEditorReviewRoute" Component={PubEditorReviewRoute} path={SPALink('license/verification/')} />,
    <Route
        key="LicenseSigning"
        Component={withFinishLogin(LicenseSigningRoute)}
        path={SPALink('license-signing/:articleId/:step?/:substep?/*')}
    />,
    <Route
        key="OrdersFocusOnArticle"
        Component={withFinishLogin(OrdersFocusOnArticleRoute)}
        path={SPALink('orders/focusOnArticle/:articleId')}
    />,
    <Route key="Orders" Component={withFinishLogin(OrdersRoute)} path={SPALink('orders')} />,
    <Route key="AssignArticle" Component={withFinishLogin(AssignArticleRoute)} path={SPALink('assign-article')} />,
    <Route key="EmailAlertsPage" Component={EmailAlertsRoute} path={SPALink('alerts/:alertId')} />,
    <Route key="NoMatch" Component={NoMatchRoute} path="*" />,
];
